<!--
  One requested document = one of these cards. Three primary visual
  states stack on the same card chrome: empty (dropzone), pending
  (file picked but not yet on S3), uploaded (saved + Preview/Replace).
  UPLOADING and REPLACING share the busy overlay below the main row.
-->
<article
  class="kts-slot"
  [class.kts-slot--empty]="slot.state === 'EMPTY'"
  [class.kts-slot--pending]="slot.state === 'PENDING'"
  [class.kts-slot--uploading]="slot.state === 'UPLOADING' || slot.state === 'REPLACING'"
  [class.kts-slot--uploaded]="slot.state === 'UPLOADED'"
  [class.kts-slot--disabled]="disabled"
  [attr.aria-busy]="slot.state === 'UPLOADING' || slot.state === 'REPLACING'">

  <!-- Slot header: document name on the left, status pill on the right -->
  <header class="kts-slot__header">
    <h3 class="kts-slot__title" [title]="slot.doc.documentName">
      {{ slot.doc.documentName }}
    </h3>
    <span class="kts-slot__pill" [attr.data-state]="slot.state">
      <ng-container [ngSwitch]="slot.state">
        <ng-container *ngSwitchCase="'EMPTY'">Not uploaded</ng-container>
        <ng-container *ngSwitchCase="'PENDING'">Ready to upload</ng-container>
        <ng-container *ngSwitchCase="'UPLOADING'">Uploading…</ng-container>
        <ng-container *ngSwitchCase="'REPLACING'">Replacing…</ng-container>
        <ng-container *ngSwitchCase="'UPLOADED'">Uploaded</ng-container>
      </ng-container>
    </span>
  </header>

  <!-- ============== EMPTY state: dropzone with native picker ============== -->
  <label *ngIf="slot.state === 'EMPTY'"
         class="kts-dropzone"
         [for]="inputId()"
         [class.kts-dropzone--disabled]="disabled"
         [class.kts-dropzone--dragover]="isDragOver"
         (dragenter)="onDragOver($event)"
         (dragover)="onDragOver($event)"
         (dragleave)="onDragLeave($event)"
         (drop)="onDrop($event)">
    <mat-icon class="kts-dropzone__icon" aria-hidden="true">cloud_upload</mat-icon>
    <span class="kts-dropzone__primary">
      {{ isDragOver ? 'Drop the file here' : 'Click or drag a file to upload' }}
    </span>
    <span class="kts-dropzone__secondary">
      PDF, JPG or PNG · up to 10 MB
    </span>
    <span class="kts-dropzone__accept" aria-hidden="true">
      <span class="kts-dropzone__chip">PDF</span>
      <span class="kts-dropzone__chip">JPG</span>
      <span class="kts-dropzone__chip">PNG</span>
      <span class="kts-dropzone__chip kts-dropzone__chip--soft">≤ 10 MB</span>
    </span>
    <input type="file"
           [id]="inputId()"
           class="kts-dropzone__input"
           accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
           [disabled]="disabled"
           (change)="onFileInputChange($event)"/>
  </label>

  <!-- ============== PENDING state: file picked, not yet on S3 ============== -->
  <div *ngIf="slot.state === 'PENDING'"
       class="kts-row kts-row--pending"
       [class.kts-row--dragover]="isDragOver"
       (dragenter)="onDragOver($event)"
       (dragover)="onDragOver($event)"
       (dragleave)="onDragLeave($event)"
       (drop)="onDrop($event)">
    <mat-icon class="kts-row__icon" aria-hidden="true">description</mat-icon>
    <div class="kts-row__meta">
      <span class="kts-row__name" [title]="slot.pendingFile?.name">
        {{ slot.pendingFile?.name }}
      </span>
      <span class="kts-row__size">
        {{ formatBytes(slot.pendingFile?.size) }}
      </span>
    </div>

    <div class="kts-row__actions">
      <label class="kts-link-btn"
             [for]="inputId()"
             [class.kts-link-btn--disabled]="disabled">
        Change file
      </label>
      <button type="button"
              class="kts-link-btn kts-link-btn--danger"
              [disabled]="disabled"
              (click)="onRemoveClick()">
        Remove
      </button>
    </div>

    <!-- Hidden input that the Change file label triggers -->
    <input type="file"
           [id]="inputId()"
           class="kts-row__hidden-input"
           accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
           [disabled]="disabled"
           (change)="onFileInputChange($event)"/>

    <p class="kts-row__hint">
      File ready. It will be uploaded when you click Submit below.
    </p>
  </div>

  <!-- ============== UPLOADING / REPLACING state: progress overlay ========= -->
  <div *ngIf="slot.state === 'UPLOADING' || slot.state === 'REPLACING'"
       class="kts-row kts-row--uploading">
    <mat-progress-spinner mode="indeterminate" diameter="22"></mat-progress-spinner>
    <div class="kts-row__meta">
      <span class="kts-row__name" [title]="slot.pendingFile?.name">
        {{ slot.pendingFile?.name || slot.doc.uploadedFileName }}
      </span>
      <span class="kts-row__size">
        Sending to secure storage…
      </span>
    </div>
  </div>

  <!-- ============== UPLOADED state: saved file + Preview / Replace ========= -->
  <div *ngIf="slot.state === 'UPLOADED'"
       class="kts-row kts-row--uploaded"
       [class.kts-row--dragover]="isDragOver"
       (dragenter)="onDragOver($event)"
       (dragover)="onDragOver($event)"
       (dragleave)="onDragLeave($event)"
       (drop)="onDrop($event)">
    <mat-icon class="kts-row__icon kts-row__icon--ok"
              aria-hidden="true">picture_as_pdf</mat-icon>
    <div class="kts-row__meta">
      <span class="kts-row__name" [title]="slot.doc.uploadedFileName">
        {{ slot.doc.uploadedFileName }}
      </span>
      <span class="kts-row__size">
        {{ formatBytes(slot.doc.uploadedFileSizeBytes) }}
      </span>
    </div>

    <div class="kts-row__actions">
      <button type="button"
              class="kts-link-btn"
              [disabled]="disabled"
              (click)="onPreviewClick()">
        <mat-icon aria-hidden="true">visibility</mat-icon>
        Preview
      </button>
      <label class="kts-link-btn kts-link-btn--accent"
             [for]="inputId()"
             [class.kts-link-btn--disabled]="disabled">
        Replace file
      </label>
    </div>

    <!-- Replace uses the same hidden input pattern; picking a file flips
         the slot back to PENDING in the parent. -->
    <input type="file"
           [id]="inputId()"
           class="kts-row__hidden-input"
           accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
           [disabled]="disabled"
           (change)="onFileInputChange($event)"/>

    <p class="kts-row__hint">
      The buyer will see this file once you click Submit.
    </p>
  </div>

  <!-- ============== Inline error (oversized file, mid-upload fail) ========= -->
  <div *ngIf="slot.errorMessage" class="kts-error" role="alert">
    <mat-icon aria-hidden="true">error_outline</mat-icon>
    <span class="kts-error__text">{{ slot.errorMessage }}</span>
    <button *ngIf="slot.state === 'PENDING' && slot.errorMessage"
            type="button"
            class="kts-link-btn kts-link-btn--accent"
            (click)="onRetryClick()">
      Retry
    </button>
  </div>

</article>
